<template>
    <div>
        <div is="sui-header" text-align="right">
            <sui-button @click="addDefaultInstructions">{{ $t('common.reset') }}</sui-button>
            <sui-button @click="showCopy = true">{{ $t('common.copyFrom') }}</sui-button>
            <sui-button color="violet" @click="saveGatewayInstructions"
                        :disabled="activeGatewayInstructions.length === 0">
                {{ $t('common.save') }}
            </sui-button>
        </div>
        <div class="large-content">
            <sui-grid :columns="2">
                <sui-grid-row>
                    <sui-grid-column :width="7">
                        <sui-segment>
                            <h1 is="sui-header">{{ $t('common.instructions') }}</h1>

                            <sui-message info>
                                <p>{{ $t('gateway.instructions.explication1') }}</p>
                                <p>{{ $t('gateway.instructions.explication2') }}</p>
                                <p>{{ $t('gateway.instructions.explication3') }}</p>
                            </sui-message>

                            <sui-container text-align="right" class="expandAllContainer">
                                <sui-button color="violet" @click="expandAll">
                                    {{ $t('gateway.instructions.expandAll') }}
                                </sui-button>
                            </sui-container>

                            <sui-grid :columns="2">
                                <sui-grid-row>
                                    <sui-grid-column :width="6">

                                        <sui-table celled selectable class="sticky-segment">
                                            <Draggable
                                                    :group="{ name: 'instructionsList', pull: 'clone', put: false }"
                                                    class="ui list divided relaxed"
                                                    :list="instructionsMetadata"
                                                    :sort="false"
                                                    :clone="cloneInstruction"
                                                    tag="tbody"
                                            >
                                                <sui-table-row v-for="instruction of instructionsMetadata"
                                                               :key="instruction.name"
                                                               class="draggable">
                                                    <sui-table-cell>
                                                        <h4 is="sui-header">{{ instruction.name }}</h4>
                                                    </sui-table-cell>
                                                </sui-table-row>
                                            </Draggable>
                                        </sui-table>

                                    </sui-grid-column>
                                    <sui-grid-column :width="10">
                                        <sui-table class="compact borderless" celled>
                                            <Draggable
                                                    :group="{ name: 'instructionsList' }"
                                                    animation="150"
                                                    handle=".draggable"
                                                    class="ui list divided relaxed"
                                                    :list="activeGatewayInstructions"
                                                    :sort="true"
                                                    tag="tbody"
                                            >
                                                <sui-table-row v-if="activeGatewayInstructions.length === 0"
                                                               style="border: none">
                                                    <sui-table-cell text-align="center">
                                                        <sui-card class="fluid">
                                                            <sui-card-content>
                                                                <sui-icon size="big"
                                                                          name="hand point right outline"></sui-icon>
                                                                <sui-icon size="big"
                                                                          name="hand point left outline"></sui-icon>
                                                            </sui-card-content>
                                                        </sui-card>
                                                    </sui-table-cell>
                                                </sui-table-row>
                                                <sui-table-row v-else
                                                               v-for="(instruction, index) of activeGatewayInstructions"
                                                               :key="instruction.executionOrder">
                                                    <sui-table-cell style="border: none">
                                                        <sui-card class="fluid">
                                                            <sui-card-content>
                                                                <sui-card-header
                                                                        class="instruction-card-header draggable">
                                                                    <h4 is="sui-header" style="margin: 0">
                                                                        <sui-label circular color="violet">{{
                                                                                index + 1
                                                                            }}
                                                                        </sui-label>
                                                                        {{ instruction.instructionName }}
                                                                    </h4>
                                                                    <div>
                                                                        <sui-button color="red" size="mini"
                                                                                    @click="removeInstruction(index)"
                                                                                    basic
                                                                                    icon="remove"/>
                                                                    </div>
                                                                </sui-card-header>
                                                            </sui-card-content>
                                                            <sui-card-content>
                                                                <sui-accordion>
                                                                    <a is="sui-accordion-title">
                                                                        <sui-icon name="dropdown"/>
                                                                        {{ $t('common.settings') }}
                                                                    </a>
                                                                    <sui-accordion-content>
                                                                        <InstructionFormBuilder
                                                                                v-if="instructionsMetadata && instructionsMetadata.length"
                                                                                :name="instruction.instructionName"
                                                                                v-model="instruction.args"
                                                                                :errors.sync="errors[index]"
                                                                                :instructionsMetadata="instructionsMetadata"
                                                                        />
                                                                    </sui-accordion-content>
                                                                </sui-accordion>
                                                            </sui-card-content>
                                                        </sui-card>
                                                    </sui-table-cell>
                                                </sui-table-row>
                                            </Draggable>
                                        </sui-table>

                                        <sui-button basic class="right floated" @click="scrollToTop"
                                                    icon="arrow circle up" label-position="right">
                                            {{ $t('gateway.instructions.scrollToTop') }}
                                        </sui-button>
                                    </sui-grid-column>
                                </sui-grid-row>
                            </sui-grid>
                        </sui-segment>
                    </sui-grid-column>

                    <sui-grid-column :width="9">

                        <sui-segment class="sticky-segment">
                            <h1 is="sui-header">{{ $t('common.preview') }}</h1>

                            <sui-message info>
                                <p>{{ $t('gateway.instructions.preview.explication1') }}</p>
                                <p>{{ $t('gateway.instructions.preview.explication2') }}</p>
                            </sui-message>

                            <div v-if="!testFileOriginal.file">
                                <div class="ui fluid action input">
                                    <sui-button disabled>{{ $t('gateway.instructions.sampleFile') }}</sui-button>
                                    <sui-input type="file" @change="previewFiles" required></sui-input>
                                </div>
                            </div>
                            <div v-if="testFileOriginal.file">
                                <p class="ui fluid action input">
                                    <sui-button disabled>{{ $t('gateway.instructions.sampleFile') }}</sui-button>
                                    <sui-input :placeholder="testFileOriginal.file.name" icon="file" disabled/>
                                    <sui-button @click="removeFile" icon="remove"/>
                                </p>

                                <sui-message warning
                                             v-if="activeGateway.configuration.fileConfiguration.fileType.toUpperCase() !== testFileOriginal.file.type.split('/').pop().toUpperCase()">
                                    <p>
                                        <sui-icon name="triangle exclamation"/>
                                        {{
                                            $t('gateway.instructions.preview.warning.fileTypeNotSimilar', {
                                                gatewayFileType: activeGateway.configuration.fileConfiguration.fileType,
                                                fileType: testFileOriginal.file.type
                                            })
                                        }}
                                    </p>
                                </sui-message>

                                <sui-tab active-index="2">
                                    <sui-tab-pane :title="$t('gateway.instructions.preview.source')">

                                        <MonacoEditor
                                                width="100%"
                                                height="70vh"
                                                theme="vs-dark"
                                                v-model="testFileOriginal.content"
                                                language="json"
                                        ></MonacoEditor>

                                    </sui-tab-pane>
                                    <sui-tab-pane :title="$t('gateway.instructions.preview.fullResult')">

                                        <MonacoEditor
                                                width="100%"
                                                height="70vh"
                                                theme="vs-dark"
                                                v-model="testFileResult.content"
                                                language="json"
                                        ></MonacoEditor>

                                    </sui-tab-pane>
                                    <sui-tab-pane :title="$t('gateway.instructions.preview.firstMandateResult')">

                                        <MonacoEditor
                                                width="100%"
                                                height="70vh"
                                                theme="vs-dark"
                                                v-model="testFileResult.first"
                                                language="json"
                                        ></MonacoEditor>

                                    </sui-tab-pane>
                                    <sui-tab-pane :title="$t('gateway.instructions.preview.comparison')">

                                        <MonacoEditor
                                                width="100%"
                                                height="70vh"
                                                theme="vs-dark"
                                                :diffEditor="true"
                                                :value="testFileResult.content"
                                                :original="testFileOriginal.content"
                                                language="json"
                                        ></MonacoEditor>

                                    </sui-tab-pane>
                                </sui-tab>
                            </div>
                        </sui-segment>

                    </sui-grid-column>
                </sui-grid-row>

                <sui-modal v-model="showCopy">
                    <sui-modal-header>{{ $t('gateway.instructions.copyFrom') }}</sui-modal-header>
                    <sui-modal-content>
                        <sui-dropdown
                                selection
                                fluid
                                :options="gatewaysOptions"
                                v-model="copyFrom"
                        />
                    </sui-modal-content>
                    <sui-modal-actions>
                        <sui-button positive @click.native="onCopy()">{{ $t('common.copy') }}</sui-button>
                        <sui-button @click.native="showCopy = false">{{ $t('common.cancel') }}</sui-button>
                    </sui-modal-actions>
                </sui-modal>
            </sui-grid>
        </div>
    </div>
</template>

<script>
import store from '@/store';
import {api} from '@/api';
import Draggable from 'vuedraggable';
import InstructionFormBuilder from '@/components/gateway-instruction/InstructionFormBuilder.vue';
import {mapGetters} from 'vuex';
import MonacoEditor from 'monaco-editor-vue';
import debounce from 'debounce';

export default {
    name: 'GatewayInstructionsForm',
    components: {
        Draggable,
        MonacoEditor,
        InstructionFormBuilder,
    },
    beforeCreate() {
        store.commit('setActiveGateway');
        store.commit('setActiveGatewayInstructions', []);
    },
    beforeDestroy() {
        store.commit('setActiveGateway');
        store.commit('setActiveGatewayInstructions', []);
        store.commit('removeAppTitle');
    },
    mounted() {
        store.commit('setAppTitle', `${this.$t('common.instructions')}`);
    },
    data() {
        return {
            isLoading: false,
            modalContent: null,
            errors: [],
            showCopy: false,
            copyFrom: '',
            instructionsMetadata: [],
            testFileOriginal: {
                file: null,
                content: ''
            },
            testFileResult: {
                content: '',
                first: ''
            },
            isAllExpanded: false,
        }
    },
    computed: {
        ...mapGetters([
            'activeGateway',
            'activeGatewayInstructions',
            'gatewayInstructions',
        ]),
        errorsCount() {
            return this.errors.reduce((prev, value) => {
                return prev + (value || 0);
            }, 0);
        },
        gatewaysOptions() {
            return this.$store.state.gateways.map(item => {
                return {
                    text: item.name,
                    value: item.id,
                };
            }).filter(item => item.value !== this.$route.params.gateway_id);
        }
    },
    watch: {
        activeGatewayInstructions: {
            handler() {
                this.handlerInstructionsChanged();
            },
            immediate: true,
            deep: true
        },
    },
    async created() {
        this.handlerInstructionsChanged = debounce(this.handlerInstructionsChanged, 1000);

        await store.dispatch('getGateways');
        await store.dispatch('getGatewayById', this.$route.params.gateway_id);
        await store.dispatch('getGatewaysInstructions');
        await store.dispatch('getGatewayInstructionsById', this.$route.params.gateway_id);
        await this.fetchInstructionMetadatas();

        store.commit('setAppTitle', `${this.$t('common.instructions')} - ${this.activeGateway.name}`);
    },
    methods: {
        previewFiles(event) {
            this.testFileOriginal.file = event.target.files[0];

            this.testSampleFile();
        },
        removeFile() {
            this.testFileOriginal.file = null;
            this.testFileOriginal.content = null;
            this.testFileResult.content = null;
            this.testFileResult.first = null;
        },
        async saveGatewayInstructions() {
            if (this.errorsCount) {
                this.$toastr.e(this.$t('gateway.instructions.incompleteData'));
                return;
            }

            await store.dispatch('saveGatewayInstructions', this.$route.params.gateway_id);

            this.$toastr.s(this.$t('gateway.instructions.saveSuccess'));
        },
        async testSampleFile() {
            try {
                this.isLoading = true;

                if (!this.testFileOriginal.file) {
                    return;
                }

                const normalizedInstructions = this.activeGatewayInstructions.map((instruction, index) => ({
                    execution_order: index,
                    name: instruction.instructionName,
                    args: instruction.args,
                }));

                let fileTestResult = null;

                fileTestResult = await api.gateways.testSampleFile(this.$route.params.gateway_id, normalizedInstructions, this.testFileOriginal.file);
                if (!fileTestResult) {
                    throw new Error('empty data for preview');
                }

                const originalContent = fileTestResult.original;
                const convertedContent = fileTestResult.content;

                this.testFileOriginal.content = JSON.stringify(originalContent, null, 4);
                this.testFileResult.content = JSON.stringify(convertedContent, null, 4);
                this.testFileResult.first = JSON.stringify(convertedContent[0], null, 4);

                this.isLoading = false;
            } catch (err) {
                this.$toastr.e(this.$t('gateway.instructions.preview.error'));
            }
        },
        addDefaultInstructions() {
            const defaultInstructions = ['CUT_BY_ITEM', 'DETERMINE_ID', 'FILE_EXTRACTION'].map(this.makeGatewayInstruction);

            store.commit('updateActiveGatewayInstructions', defaultInstructions);
        },
        expandAll() {
            const elements = document.querySelectorAll('.title, .content');
            elements.forEach((element) => {
                this.isAllExpanded ? element.classList.remove('active') : element.classList.add('active');
            });
            this.isAllExpanded = !this.isAllExpanded;
        },
        updateFileExtractionProperty(action, property, instructionIndex) {
            const data = JSON.parse(JSON.stringify(this.activeGatewayInstructions));

            if (action === 'ADD') {
                data[instructionIndex].args[0][property] = [''];
            }

            if (action === 'DELETE') {
                delete data[instructionIndex].args[0][property];
            }

            store.commit('updateActiveGatewayInstructions', data);
        },
        getDefaultInstructionArgs(instructionName) {
            switch (instructionName) {
                case 'APPEND_PREPEND_VALUE':
                    return [[''], 'APPEND', ''];
                case 'REPLACE_VALUES':
                    return [[''], [['', '']]];
                case 'RENAME_KEYS':
                    return [[''], ''];
                case 'RENAME_ARRAY_KEY':
                    return ['', '', ['']];
                case 'FIELD_FUSION':
                    return ['', ['']];
                case 'FLATTEN':
                    return ['', ''];
                case 'FILE_EXTRACTION':
                    return [{}];
                case 'COPY_AND_REPLACE_VALUES':
                    return ['', '', [['', '']]];
                case 'EXTRACT_ARRAY_FIELD':
                    return ['', ['']];
                case 'SUM_VALUES':
                    return [[''], ''];
                default:
                    return [];
            }
        },
        makeGatewayInstruction(instructionName) {
            return {
                instructionName: instructionName,
                args: this.getDefaultInstructionArgs(instructionName),
            }
        },
        cloneInstruction(instruction) {
            return this.makeGatewayInstruction(instruction.name);
        },
        removeInstruction(index) {
            this.activeGatewayInstructions.splice(index, 1);
        },
        async onCopy() {
            if (!this.copyFrom) {
                this.$toastr.e(this.$t('gateway.instructions.selectGateway'));
                return;
            }
            const data = await api.gateways.getInstructionsById(this.copyFrom);
            this.$store.commit('setActiveGatewayInstructions', data);
            this.showCopy = false;
        },
        async fetchInstructionMetadatas() {
            try {
                this.instructionsMetadata = await api.gateways.getInstructionsMetadata();
            } catch (err) {
                // Silent fail
            }
        },
        handlerInstructionsChanged() {
            this.testSampleFile();
        },
        scrollToTop() {
            window.scrollTo(0, 0);
        }
    }
}
</script>

<style scoped>
.formated {
    font-family: "Consolas" !important;
}

.draggable:hover {
    cursor: grab;
}

.draggable:active {
    cursor: grabbing;
}

table.borderless {
    border: none;
}

table.borderless tbody tr:first-of-type td:first-of-type {
    padding-top: 0;
}

.instruction-card-header {
    display: flex !important;
    justify-content: space-between;
    align-items: center;
    margin-top: 0px !important;
}

.expandAllContainer {
    margin-bottom: 10px;
    padding-right: 0.5em;
}

.sticky-segment {
    top: 20px;
    position: sticky !important;
}
</style>
